import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { Chat, MessageInput } from "@pubnub/react-chat-components";

import { EvotruxDivider } from "components/EvotruxDivider";
import { useChatPublishToken } from "hooks";
import { getUserEntity } from "utils/chat";

import { MessageHistoryDisplay } from "./components/MessageHistoryDisplay";
import { withCustomerAppSource } from "./utils";

import type {
  MessageEnvelope,
  MessagePayload,
} from "@pubnub/react-chat-components";
import type { UriFileInput } from "pubnub";

interface Props {
  currentChannel: string;
  channelGroup: UserChannelGroup;
  user: User;
  userPubnubData: PubnubClientData;
  onMessageSent: (value: MessagePayload | File | UriFileInput) => void;
  conversationHeader: React.ReactNode;
  onNewMessage: (message: MessageEnvelope) => void;
  useDesktopLayout: boolean;
  isStandaloneChat?: boolean;
}

const CONVERSATION_CONTAINER_ID = "conversation-container";
const AT_BOTTOM_THRESHOLD_PX = 80;

export function ChatConversation(props: Props) {
  const {
    currentChannel,
    channelGroup,
    user,
    userPubnubData,
    onMessageSent,
    conversationHeader,
    onNewMessage,
    useDesktopLayout,
    isStandaloneChat = false,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [showNewMessageBanner, setShowNewMessageBanner] =
    useState<boolean>(false);
  const { canSend, handlePubnubStatus } = useChatPublishToken({
    currentChannel,
  });

  useEffect(() => {
    function handleResize() {
      if (!isStandaloneChat) {
        const windowHeight = window.innerHeight;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const containerTop = containerRef.current.offsetTop;
        const containerMargin = 100; // adjust as needed
        const containerMaxHeight =
          windowHeight - containerTop - containerMargin;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        containerRef.current.style.minHeight = `${containerMaxHeight}px`;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        containerRef.current.style.maxHeight = `${containerMaxHeight}px`;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        containerRef.current.style.overflowY = "auto";
      }
    }

    handleResize(); // set initial height
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isStandaloneChat]);

  const scrollToBottomOnChat = useCallback(() => {
    const container = document.getElementById(CONVERSATION_CONTAINER_ID);
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  const isNearBottom = useCallback((): boolean => {
    const container = document.getElementById(CONVERSATION_CONTAINER_ID);
    if (!container) return true;
    const { scrollTop, clientHeight, scrollHeight } = container;
    return scrollTop + clientHeight >= scrollHeight - AT_BOTTOM_THRESHOLD_PX;
  }, []);

  const handleScrollToNewMessage = useCallback(() => {
    scrollToBottomOnChat();
    setShowNewMessageBanner(false);
  }, [scrollToBottomOnChat]);

  const handleScroll = useCallback(() => {
    if (isNearBottom()) setShowNewMessageBanner(false);
  }, [isNearBottom]);

  const onMessage = useCallback(
    (message: MessageEnvelope) => {
      if (message?.channel && message.channel === currentChannel) {
        if (isNearBottom()) {
          // Defer until after React renders the new message into the DOM.
          setTimeout(scrollToBottomOnChat, 0);
        } else {
          setShowNewMessageBanner(true);
        }
      }
      onNewMessage(message);
    },
    [currentChannel, onNewMessage, isNearBottom, scrollToBottomOnChat],
  );

  // Stable memo refs prevent <Chat> from re-subscribing to PubNub on every render.
  const channelGroupsArray = useMemo(
    () => [channelGroup.name],
    [channelGroup.name],
  );

  const usersArray = useMemo(
    () => [getUserEntity(user, userPubnubData)],
    [user, userPubnubData],
  );

  useEffect(() => {
    if (!isStandaloneChat) {
      containerRef.current?.scrollIntoView();
    }
  }, [isStandaloneChat]);

  // Switching conversations reuses this pane without remounting, so the banner
  // must be cleared explicitly or it would carry over from the previous channel.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowNewMessageBanner(false);
  }, [currentChannel]);

  return (
    <Stack
      borderRadius={2}
      boxShadow={2}
      id="chat-pane"
      direction="column"
      width={useDesktopLayout ? "70%" : "100%"}
      minHeight="100xp"
    >
      {conversationHeader}
      <EvotruxDivider />
      <Chat
        theme="light"
        currentChannel={currentChannel}
        channelGroups={channelGroupsArray}
        users={usersArray}
        onMessage={onMessage}
        onStatus={handlePubnubStatus}
        enablePresence={false}
      >
        <Box id="msg-and-input-container">
          <Box sx={{ position: "relative" }}>
            <Box
              id={CONVERSATION_CONTAINER_ID}
              ref={containerRef}
              onScroll={handleScroll}
              sx={{
                tabIndex: 0,
                minHeight: "200px",
                maxHeight: "500px",
                overflowY: "scroll",
              }}
            >
              <MessageHistoryDisplay
                currentChannel={currentChannel}
                onHistoryLoaded={scrollToBottomOnChat}
              />
            </Box>
            {showNewMessageBanner && (
              <Tooltip title="Scroll to latest message">
                <Button
                  onClick={handleScrollToNewMessage}
                  variant="contained"
                  size="small"
                  sx={{
                    position: "absolute",
                    bottom: 8,
                    left: "50%",
                    transform: "translateX(-50%)",
                    borderRadius: "20px",
                    whiteSpace: "nowrap",
                    zIndex: 10,
                  }}
                >
                  New message ↓
                </Button>
              </Tooltip>
            )}
          </Box>
          <Box id="chat-input-boxs">
            <MessageInput
              typingIndicator
              fileUpload="all"
              senderInfo
              onBeforeSend={withCustomerAppSource}
              onSend={onMessageSent}
              disabled={!canSend}
              placeholder={
                canSend ? "Send message" : "Preparing this conversation…"
              }
            />
          </Box>
          <Typography
            sx={{ ml: "1rem" }}
            variant="body2"
            color="text.secondary"
          >
            Max file upload size: 5MB | Press Shift + Enter to add a line break
          </Typography>
        </Box>
      </Chat>
    </Stack>
  );
}
